import { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { readStoredConsent } from '../lib/cookieConsent';

const GTM_ID = String(process.env.REACT_APP_GTM_ID || '').trim();
const GA_MEASUREMENT_ID = String(process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const REQUIRE_ANALYTICS_CONSENT = String(process.env.REACT_APP_ANALYTICS_REQUIRE_CONSENT || 'false')
  .trim()
  .toLowerCase() === 'true';

function ensureDataLayer() {
  if (typeof window === 'undefined') return [];
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
  return window.dataLayer;
}

function applyConsent(preferences = {}) {
  if (typeof window === 'undefined') return;
  ensureDataLayer();
  const analyticsGranted = REQUIRE_ANALYTICS_CONSENT ? Boolean(preferences.analytics) : true;
  const marketingGranted = Boolean(preferences.marketing);
  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: marketingGranted ? 'granted' : 'denied',
    ad_user_data: marketingGranted ? 'granted' : 'denied',
    ad_personalization: marketingGranted ? 'granted' : 'denied',
  });
  window.dataLayer.push({
    event: 'vn_cookie_consent_update',
    consent_analytics: analyticsGranted,
    consent_marketing: marketingGranted,
  });
}

function injectTagManager() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !GTM_ID) return;
  if (document.getElementById('vn-gtm-script')) return;

  ensureDataLayer();
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.id = 'vn-gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

export default function GoogleTagManager() {
  const location = useLocation();
  const [consentPreferences, setConsentPreferences] = useState(null);
  const currentPath = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !GTM_ID) return undefined;

    ensureDataLayer();
    window.gtag('consent', 'default', {
      analytics_storage: REQUIRE_ANALYTICS_CONSENT ? 'denied' : 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });

    injectTagManager();

    const storedConsent = readStoredConsent();
    if (storedConsent?.preferences) {
      setConsentPreferences(storedConsent.preferences);
      applyConsent(storedConsent.preferences);
    } else if (!REQUIRE_ANALYTICS_CONSENT) {
      const impliedPreferences = { necessary: true, analytics: true, marketing: false };
      setConsentPreferences(impliedPreferences);
      applyConsent(impliedPreferences);
    }

    const handleConsentUpdate = (event) => {
      const nextPreferences = event?.detail?.preferences || {};
      setConsentPreferences(nextPreferences);
      applyConsent(nextPreferences);
    };

    window.addEventListener('vn-cookie-consent-updated', handleConsentUpdate);
    return () => window.removeEventListener('vn-cookie-consent-updated', handleConsentUpdate);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !GTM_ID) return;
    if (REQUIRE_ANALYTICS_CONSENT && !consentPreferences?.analytics) return;
    ensureDataLayer();
    const payload = {
      event: 'vn_virtual_page_view',
      page_path: currentPath,
      page_location: window.location.href,
      page_title: document.title,
    };
    if (GA_MEASUREMENT_ID) {
      payload.ga_measurement_id = GA_MEASUREMENT_ID;
    }
    window.dataLayer.push(payload);
  }, [consentPreferences?.analytics, currentPath]);

  return null;
}
